import type { StreamMessage } from '@/hooks/useChatStream';
import React from 'react';
import { Check, Copy, FileText, Sparkles, Table } from 'lucide-react';
import { MathMarkdown } from './MathMarkdown';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import { formatDayLabel, formatTimeISO, isSameDay } from '@/lib/date';
import { EmptyState } from '@/components/ui/empty-state';

interface MessageItemProps {
  message: StreamMessage;
  copiedId?: number | null;
  streaming?: boolean;
  onCopy?: (id: number, content: string) => void;
  assistantLabel: string;
  hideHeader?: boolean;
  headerBadgeText?: string | null;
}
const MessageItem = function ({
  message,
  copiedId,
  streaming,
  onCopy,
  assistantLabel,
  hideHeader,
  headerBadgeText,
}: MessageItemProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  const ts = new Date(message.created_at);
  const time = formatTimeISO(ts);

  // Contenitore: niente bubble per l'assistente, bubble per l'utente
  const containerClass = isAssistant
    ? 'block w-full max-w-[70ch] text-foreground'
    : cn(
        'inline-block rounded-2xl leading-7 align-top',
        'bg-primary px-3 py-2 text-primary-foreground max-w-[70ch]',
      );

  return (
    <li
      className={cn(
        'relative w-[calc(100%-2.5rem)]',
        isUser ? 'text-right ms-10' : 'text-left me-10',
      )}
    >
      <div className={containerClass}>
        {message.image_url &&
          (message.content_type && message.content_type.startsWith('image/') ? (
            <img src={message.image_url} alt="allegato" className="max-w-xs rounded mb-2 border" />
          ) : (
            <div className="mb-2 inline-flex items-center gap-2 text-sm">
              {message.filename?.match(/\.(xls|xlsx)$/i) ||
              message.content_type?.includes('sheet') ? (
                <Table className="w-5 h-5 text-muted-foreground" />
              ) : (
                <FileText className="w-5 h-5 text-muted-foreground" />
              )}
              <a
                href={message.image_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-muted-foreground hover:text-foreground"
              >
                {message.filename || 'Allegato'}
              </a>
            </div>
          ))}
        {isAssistant ? (
          <>
            {!hideHeader && (
              <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground">
                <span>{assistantLabel}</span>
                {headerBadgeText && (
                  <Badge variant="secondary" className={cn(streaming && 'animate-pulse')}>
                    {headerBadgeText}
                  </Badge>
                )}
              </div>
            )}
            <div
              className={cn(
                'prose prose-sm max-w-none flex flex-col gap-2',
                streaming && 'animate-pulse',
              )}
              aria-live={streaming ? 'polite' : undefined}
              role={streaming ? 'status' : undefined}
            >
              <MathMarkdown>{message.content}</MathMarkdown>
            </div>
            {!streaming && onCopy && (
              <div className="flex items-center gap-3 mt-2 text-muted-foreground text-[10px]">
                {time && <span>{time}</span>}
                <button
                  type="button"
                  title="Copia"
                  aria-label="Copia messaggio"
                  className="p-1 rounded hover:bg-muted-foreground/20 transition cursor-pointer"
                  onClick={() => onCopy?.(message.id, message.content)}
                >
                  {copiedId === message.id ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-end">
            <span className="whitespace-pre-line text-left inline-block max-w-[70ch]">
              {message.content}
            </span>
          </div>
        )}
      </div>
      {/* Timestamp fuori dalla bubble per i messaggi utente, coerente con quelli del bot */}
      {!isAssistant && time && <div className="mt-1 text-[10px] text-muted-foreground">{time}</div>}
    </li>
  );
};

export interface MessageListProps {
  messages: StreamMessage[];
  streamingMsg: string;
  copiedId?: number | null;
  onCopy?: (id: number, content: string) => void;
  bottomRef?: React.RefObject<HTMLDivElement | null>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  assistantLabel: string;
  loading?: boolean;
}

function MessageList({
  messages,
  streamingMsg,
  copiedId,
  onCopy,
  bottomRef,
  containerRef,
  onScroll,
  assistantLabel,
  loading,
}: MessageListProps) {
  // Funzioni utili per separatori giorno
  let lastDay: Date | null = null;

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="flex-1 min-h-0 overflow-y-auto scrollbar-transparent w-full pr-1"
    >
      {messages.length === 0 && !streamingMsg && !loading ? (
        <EmptyState
          title="Prenditi tutto il tempo per iniziare la conversazione"
          description="Il tuo assistente personale è qui per accompagnarti nel tuo percorso di crescita."
          icon={<Sparkles className="h-8 w-8" aria-hidden />}
        />
      ) : (
        <ul className="space-y-8">
          {messages.map((message) => {
            const current = new Date(message.created_at);
            const showDivider = !lastDay || !isSameDay(current, lastDay);
            lastDay = current;
            const messageItem = (
              <MessageItem
                message={message}
                copiedId={copiedId}
                onCopy={onCopy}
                assistantLabel={assistantLabel}
              />
            );

            const divider = (
              <li className="flex items-center justify-center text-xs text-muted-foreground">
                <span className="px-3 py-0.5 rounded-full bg-muted/30 dark:bg-muted/10 border">
                  {formatDayLabel(current)}
                </span>
              </li>
            );

            return showDivider ? (
              <React.Fragment key={message.id}>
                {divider}
                {messageItem}
              </React.Fragment>
            ) : (
              messageItem
            );
          })}
          {streamingMsg ? (
            <MessageItem
              streaming
              message={{
                role: 'assistant',
                content: streamingMsg,
                id: 0,
                created_at: new Date().toISOString(),
              }}
              assistantLabel={assistantLabel}
              headerBadgeText="Sto scrivendo..."
            />
          ) : (
            loading && (
              <MessageItem
                streaming
                message={{
                  role: 'assistant',
                  content: '',
                  id: 0,
                  created_at: new Date().toISOString(),
                }}
                assistantLabel={assistantLabel}
                headerBadgeText="Sto pensando..."
              />
            )
          )}
          <div className="-mt-8" ref={bottomRef} />
        </ul>
      )}
    </div>
  );
}

export default MessageList;
